import React, { useCallback, useEffect } from "react";

// types
import { CouponConfetti, CouponMessageCard, OfferCardType } from "@swiggy-private/cart-types";
import { CouponPageType } from "@swiggy-private/checkout-core";

// helpers
import { getIconPath } from "@cart-ui/helpers/CouponModal";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// components
import { CartModal } from "../cart-modal";
import { CartModalMaxSavings } from "../cart-modal-max-savings";

export interface CouponModalProps {
    couponSuccessData?: CouponMessageCard;
    couponErrorData?: CouponMessageCard;
    closeModal: VoidFunction;
    onClickCouponSavingsPopup: (context: string) => void;
    onClickCouponSavingsPopupWithPOR?: (text: string) => void;
    triggerConfettiByType?: (type: CouponConfetti) => void;
    isCouponAutoApplied: boolean;
    impressionAutoApplyCouponPopup?: VoidFunction;
    impressionOffercashSuccessPopup?: (isPhCallout?: boolean) => void;
    showCouponConfetti?: boolean;
    isNewPORFlow?: boolean;
    goToCouponListing?: (couponListingType: CouponPageType) => void;
    onImpressionMoreOffersStrip?: (data: Record<string, unknown>) => void;
    onClickMoreOffersStrip?: VoidFunction;
    onClickCouponMaxSavingsPopup?: (text: string) => void;
}

const COUPON_APPLY_CONTEXT = {
    SUCCESS: "coupon_apply_success",
    ERROR: "coupon_apply_error",
};

const DEFAULT_COUPON_MODAL_TIMEOUT = 6000;

const CouponModal: React.FC<CouponModalProps> = ({
    couponSuccessData,
    couponErrorData,
    closeModal,
    onClickCouponSavingsPopup,
    onClickCouponSavingsPopupWithPOR,
    triggerConfettiByType,
    isCouponAutoApplied,
    impressionAutoApplyCouponPopup,
    impressionOffercashSuccessPopup,
    showCouponConfetti,
    isNewPORFlow,
    goToCouponListing,
    onImpressionMoreOffersStrip,
    onClickMoreOffersStrip,
    onClickCouponMaxSavingsPopup,
}: CouponModalProps) => {
    const onCloseModal = useCallback(() => {
        onClickCouponSavingsPopup &&
            onClickCouponSavingsPopup(
                isEmpty(couponSuccessData) ? COUPON_APPLY_CONTEXT.ERROR : COUPON_APPLY_CONTEXT.SUCCESS,
            );
        closeModal();
    }, [closeModal, onClickCouponSavingsPopup, couponSuccessData]);

    useEffectOnce(() => {
        if (!isEmpty(couponSuccessData) && showCouponConfetti) {
            const isPHApplyCoupon = couponSuccessData?.offer_card_type === OfferCardType.OFFER_CARD_TYPE_POCKET_HERO;
            triggerConfettiByType?.(CouponConfetti.AUTO_APPLY);

            if (isCouponAutoApplied) {
                impressionAutoApplyCouponPopup?.();
            }

            // secondary savings are applicable for offercash
            if (!isEmpty(couponSuccessData.secondarySavingsInfo)) {
                impressionOffercashSuccessPopup?.(isPHApplyCoupon);
            }
        }
    });

    useEffect(() => {
        let couponTimeout: number;
        if (isCouponAutoApplied) {
            couponTimeout = window.setTimeout(() => {
                closeModal();
            }, DEFAULT_COUPON_MODAL_TIMEOUT);
        }

        return (): void => clearTimeout(couponTimeout);
    }, [closeModal, isCouponAutoApplied]);

    const ModalData = useCallback(() => {
        if (!isEmpty(couponSuccessData)) {
            const data = { ...couponSuccessData, icon: getIconPath(couponSuccessData, !isNewPORFlow) };
            if (couponSuccessData.offer_card_type === OfferCardType.OFFER_CARD_TYPE_MAX_SAVINGS) {
                return (
                    <CartModalMaxSavings
                        data={data}
                        closeModal={onCloseModal}
                        onClickCouponMaxSavingsPopup={onClickCouponMaxSavingsPopup}
                    />
                );
            }
            return (
                <CartModal
                    data={data}
                    closeModal={onCloseModal}
                    isNewPORFlow={isNewPORFlow}
                    goToCouponListing={goToCouponListing}
                    onImpressionMoreOffersStrip={onImpressionMoreOffersStrip}
                    onClickMoreOffersStrip={onClickMoreOffersStrip}
                    onClickCouponSavingsPopupWithPOR={onClickCouponSavingsPopupWithPOR}
                />
            );
        }

        if (!isEmpty(couponErrorData)) {
            const data = { ...couponErrorData, icon: getIconPath(couponErrorData) };
            return <CartModal data={data} closeModal={onCloseModal} />;
        }

        return null;
    }, [
        couponSuccessData,
        couponErrorData,
        onCloseModal,
        isNewPORFlow,
        goToCouponListing,
        onImpressionMoreOffersStrip,
        onClickMoreOffersStrip,
        onClickCouponSavingsPopupWithPOR,
    ]);

    if (couponSuccessData === undefined && couponErrorData === undefined) {
        return null;
    }
    return <ModalData />;
};

export default React.memo(CouponModal);
